import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpRadioGroup } from "../../../../gvc/testNewForm/CpRadioGroup";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpDirtyIndicatorArrayIcon } from "../../../../gvc/testNewForm/CpDirtyIndicatorArrayIconLink";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { toSelectOptions } from "../../../../../services/defaults";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { FormItemValue } from "../../../../../components/antd/form/FormItemValue";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";
import { CpDescription } from "../../../../gvc/testNewForm/CpDescription";
import { ExternalLink } from "../../../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../../../envVariables";
import { ProviderGCPInstructions } from "./provider_gcp_instructions";
import { resourceLink } from "../../../../../services/cpln";
import { DefaultOptionType } from "antd/es/select";

interface Props {}
export const GcpProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();
  const clusterName = useCpFormValue("name");

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");

  const [regionOptions, setRegionOptions] = React.useState<DefaultOptionType[]>([]);
  const [networkOptions, setNetworkOptions] = React.useState<DefaultOptionType[]>([]);

  const [recommendedImages, setRecommendedImages] = React.useState<string[]>([]);
  React.useEffect(() => {
    fetchRecommendedOptions();
    discoverRegionOptions();
    discoverNetworkOptions();
  }, []);

  async function discoverRegionOptions() {
    try {
      const form = store.getState().form.instances[id];
      const values: Mk8sInput = form.values as any;

      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "regions",
          cluster: inputToMk8s(values),
        },
      });
      setRegionOptions(toSelectOptions(data.regions));
    } catch (e) {
      setRegionOptions([]);
    }
  }

  async function discoverNetworkOptions() {
    try {
      const form = store.getState().form.instances[id];
      const values: Mk8sInput = form.values as any;

      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "networks",
          cluster: inputToMk8s(values),
        },
      });
      const options = data.map((item: any) => ({ label: item.name, value: item.name }));
      setNetworkOptions(options);
    } catch (e) {
      setNetworkOptions([]);
    }
  }

  async function fetchRecommendedOptions() {
    try {
      const { data: schemaRes } = await request({ url: "/catalog/schema/mk8s" });
      const images: string[] = schemaRes.keys.spec.keys.provider.keys.gcp.keys.image.keys.recommended.allow;
      setRecommendedImages(images);
    } catch (e) {
      setRecommendedImages([]);
    }
  }

  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const isCreate = useCpFormValue(`meta.isCreate`);
  const imageType = useCpFormValue(`gcp.image.type`);
  const region = useCpFormValue(`gcp.region`);
  const network = useCpFormValue(`gcp.network`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div>
              <div>
                GCP Provider
                <CpErrorWarningArrayIcon
                  names={[
                    `gcp.projectId`,
                    `gcp.region`,
                    `gcp.labels`,
                    `gcp.tags`,
                    `gcp.metadata`,
                    `gcp.network`,
                    `gcp.saKeyLink`,
                    `gcp.image`,
                    `gcp.preInstallScript`,
                    `gcp.autoscaler`,
                    `gcp.networking`,
                  ]}
                />
              </div>
              <CpDescription>
                <ExternalLink label="Click here" url={`${DOCS_URL}/mk8s/gcp`} disableCopy /> to learn more about GCP
                Provider in docs
              </CpDescription>
            </div>
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`gcp.saKeyLink`}>GCP Service Account Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `A GCP secret that contains your GCP Service Account Key, it will be used to manage servers in your
                GCP account`,
                  }}
                />
              </CpLabelTooltip>
              <CpDescription>
                <div>
                  <span
                    className="color-link cursor-pointer"
                    onClick={() => {
                      drawer.setTemporary(
                        `mk8s-gcp-instructions`,
                        "How to get GCP Service Account Key?",
                        <>
                          <ProviderGCPInstructions
                            defaultSecretName={clusterName ? `${clusterName}-gcp-sa` : ""}
                            onSecretCreated={(secretName) => {
                              const itemLink = resourceLink("secret", secretName);
                              const secretState = {
                                value: secretName,
                                name: secretName,
                                link: itemLink,
                              };
                              formSetValue(id, `gcp.saKeyLink`, secretState);
                              formTriggerValidation(id);
                              drawer.closeTemporary();
                            }}
                          />
                        </>,
                        900,
                      );
                    }}
                  >
                    How to get GCP Service Account Key?
                  </span>
                </div>
              </CpDescription>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`gcp.saKeyLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "gcp" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create a GCP Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-gcp-create-secret`,
                        "Create GCP Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `gcp.saKeyLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`gcp`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`gcp.saKeyLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`gcp.projectId`}>Project ID</CpLabel>
              <CpInput name={`gcp.projectId`} style={{ width: 600 }} />
              <CpErrorWarning name={`gcp.projectId`} />
            </CpFormItem>
            {isCreate ? (
              <CpFormItem>
                <CpLabel name={`gcp.region`}>Region</CpLabel>
                {regionOptions.length > 0 ? (
                  <CpSelect name={`gcp.region`} options={regionOptions} style={{ maxWidth: 600 }} />
                ) : (
                  <Space.Compact>
                    <CpInput name={`gcp.region`} style={{ width: 600 }} />
                    <Tooltip title={"Browse"}>
                      <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("region")} />
                    </Tooltip>
                  </Space.Compact>
                )}
                <CpErrorWarning name={`gcp.region`} />
              </CpFormItem>
            ) : (
              <div className="mb-8">
                <CpFormItem>
                  <CpLabel name="skip">Region</CpLabel>
                  <FormItemValue>{region}</FormItemValue>
                </CpFormItem>
              </div>
            )}
            <CpFormItem>
              <CpLabel name="skip">Image</CpLabel>
              <CpRadioGroup
                name={`gcp.image.type`}
                style={{ width: 600 }}
                options={[
                  { label: "Exact", value: "exact" },
                  { label: "Recommended", value: "recommended" },
                  { label: "Family", value: "family" },
                ]}
              />
              {imageType === "exact" ? (
                <>
                  <CpInput name={`gcp.image.exact`} style={{ width: 600 }} />
                  <CpErrorWarning name={`gcp.image.exact`} />
                </>
              ) : imageType === "recommended" ? (
                <>
                  <CpSelect
                    name={`gcp.image.recommended`}
                    style={{ width: 600 }}
                    options={toSelectOptions(recommendedImages)}
                  />
                  <CpErrorWarning name={`gcp.image.recommended`} />
                </>
              ) : imageType === "family" ? (
                <>
                  <div className="flex flex-col mb-4">
                    <CpLabel name={`gcp.image.family.project`}>Project</CpLabel>
                    <CpInput name={`gcp.image.family.project`} style={{ width: 600 }} />
                    <CpErrorWarning name={`gcp.image.family.project`} />
                  </div>
                  <div className="flex flex-col mb-4">
                    <CpLabel name={`gcp.image.family.family`}>Family</CpLabel>
                    <CpInput name={`gcp.image.family.family`} style={{ width: 600 }} />
                    <CpErrorWarning name={`gcp.image.family.family`} />
                  </div>
                </>
              ) : null}
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`gcp.network`}>Network</CpLabel>
              {networkOptions.length > 0 ? (
                <CpSelect name={`gcp.network`} options={networkOptions} style={{ maxWidth: 600 }} />
              ) : (
                <Space.Compact>
                  <CpInput name={`gcp.network`} style={{ width: 600 }} />
                  <Tooltip title={"Browse"}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("networks")} />
                  </Tooltip>
                </Space.Compact>
              )}
              <CpErrorWarning name={`gcp.network`} />
            </CpFormItem>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-center gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                    <CpDirtyIndicatorArrayIcon names={["provider-gcp-advanced"]} />
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <div style={{ maxWidth: 600 }}>
                <CpArrayString label="Tags" name={`gcp.tags`} />
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label={`Labels`}
                  name={`gcp.labels`}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label={`Metadata`}
                  name={`gcp.metadata`}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <ProviderPreInstallScript provider="gcp" />
              <ProviderNetworking provider="gcp" />
              <ProviderAutoscaler provider="gcp" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "region" ? (
        <NGProviderBrowser
          title="Browse GCP Regions"
          initialValue={region}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "regions",
                cluster: inputToMk8s(values),
              },
            });
            return data.regions;
          }}
          onOk={(value) => {
            formSetValue(id, `gcp.region`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => [item, item]}
        />
      ) : null}
      {browserKey === "networks" ? (
        <NGProviderBrowser
          title="Browse GCP Networks"
          initialValue={network}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "networks",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `gcp.network`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => {
            return [item.name, item.name];
          }}
        />
      ) : null}
    </>
  );
};
